import { createFileRoute } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import { useState, useCallback } from 'react';
import { auditLogApi, type AuditLogEntry } from '@/lib/api';
import {
  FileText, Search, Filter, ChevronLeft, ChevronRight,
  Download, User, Clock, Monitor, Shield, RefreshCw,
} from 'lucide-react';
import { PageGuide } from '@/components/PageGuide';
import styles from './_auth.settings.audit-log.module.css';

export const Route = createFileRoute('/_auth/settings/audit-log')({
  component: AuditLogPage,
});

const ACTION_COLORS: Record<string, string> = {
  CREATE: '#34d399',
  UPDATE: '#60a5fa',
  DELETE: '#f87171',
};

function actionColor(action: string) {
  for (const [key, color] of Object.entries(ACTION_COLORS)) {
    if (action.includes(key)) return color;
  }
  return '#94a3b8';
}

function formatRelative(iso: string) {
  const diff = Date.now() - new Date(iso).getTime();
  if (diff < 60_000) return 'just now';
  if (diff < 3_600_000) return `${Math.floor(diff / 60_000)}m ago`;
  if (diff < 86_400_000) return `${Math.floor(diff / 3_600_000)}h ago`;
  return new Date(iso).toLocaleDateString();
}

export default function AuditLogPage() {
  const [page, setPage] = useState(1);
  const [filters, setFilters] = useState({
    resourceType: '',
    action: '',
    from: (() => { const d = new Date(); d.setDate(d.getDate() - 7); return d.toISOString().slice(0, 16); })(),
    to: new Date().toISOString().slice(0, 16),
  });
  const [applied, setApplied] = useState(filters);
  const [expanded, setExpanded] = useState<string | null>(null);
  const [exporting, setExporting] = useState(false);

  const { data, isLoading, isError, refetch } = useQuery({
    queryKey: ['audit-log', applied, page],
    queryFn: () => auditLogApi.list({
      resourceType: applied.resourceType || undefined,
      action: applied.action || undefined,
      from: applied.from ? new Date(applied.from).toISOString() : undefined,
      to: applied.to ? new Date(applied.to).toISOString() : undefined,
      page,
      limit: 25,
    }),
  });

  const applyFilters = useCallback(() => { setApplied(filters); setPage(1); }, [filters]);

  async function handleExport() {
    setExporting(true);
    try {
      const text = await auditLogApi.export(
        applied.from ? new Date(applied.from).toISOString() : undefined,
        applied.to ? new Date(applied.to).toISOString() : undefined,
      );
      const blob = new Blob([text as unknown as string], { type: 'application/x-jsonlines' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `audit-log-${new Date().toISOString().slice(0, 10)}.jsonl`;
      a.click();
      URL.revokeObjectURL(url);
    } finally {
      setExporting(false);
    }
  }

  const entries: AuditLogEntry[] = data?.entries ?? [];

  return (
    <div className={styles.page}>
      <PageGuide pageKey="audit-log" />
      {/* Header */}
      <div className={styles.header}>
        <div>
          <h1 className={styles.title}><FileText size={22} /> Audit Log</h1>
          <p className={styles.subtitle}>Every mutation — who changed what, when, from where</p>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <button className={styles.btnSecondary} onClick={() => refetch()} title="Refresh">
            <RefreshCw size={14} />
          </button>
          <button className={styles.btn} onClick={handleExport} disabled={exporting}>
            <Download size={14} /> {exporting ? 'Exporting…' : 'Export JSONL'}
          </button>
        </div>
      </div>

      {/* Filters */}
      <div className={styles.filters}>
        <div className={styles.filterField}>
          <label className={styles.filterLabel}><Filter size={12} /> Resource type</label>
          <input
            className={styles.filterInput}
            placeholder="Agent, Skill, Workflow…"
            value={filters.resourceType}
            onChange={e => setFilters(f => ({ ...f, resourceType: e.target.value }))}
            onKeyDown={e => e.key === 'Enter' && applyFilters()}
          />
        </div>
        <div className={styles.filterField}>
          <label className={styles.filterLabel}><Search size={12} /> Action keyword</label>
          <input
            className={styles.filterInput}
            placeholder="CREATE, DELETE, UPDATE…"
            value={filters.action}
            onChange={e => setFilters(f => ({ ...f, action: e.target.value }))}
            onKeyDown={e => e.key === 'Enter' && applyFilters()}
          />
        </div>
        <div className={styles.filterField}>
          <label className={styles.filterLabel}><Clock size={12} /> From</label>
          <input
            type="datetime-local"
            className={styles.filterInput}
            value={filters.from}
            onChange={e => setFilters(f => ({ ...f, from: e.target.value }))}
          />
        </div>
        <div className={styles.filterField}>
          <label className={styles.filterLabel}><Clock size={12} /> To</label>
          <input
            type="datetime-local"
            className={styles.filterInput}
            value={filters.to}
            onChange={e => setFilters(f => ({ ...f, to: e.target.value }))}
          />
        </div>
        <button className={styles.btn} onClick={applyFilters} style={{ alignSelf: 'flex-end' }}>
          <Search size={14} /> Apply
        </button>
      </div>

      {/* Summary */}
      {data && (
        <div className={styles.summary}>
          <span style={{ color: 'var(--color-text-3)', fontSize: '0.8125rem' }}>
            Showing <strong style={{ color: 'var(--color-text)' }}>{entries.length}</strong> of{' '}
            <strong style={{ color: 'var(--color-text)' }}>{data.total.toLocaleString()}</strong> entries
            &nbsp;·&nbsp; Page {data.page} of {data.pages}
          </span>
        </div>
      )}

      {/* Table */}
      <div className={styles.tableWrap}>
        {isLoading ? (
          <div className={styles.empty}>Loading…</div>
        ) : isError ? (
          <div className={styles.empty} style={{ color: '#f87171' }}>Failed to load audit log entries.</div>
        ) : entries.length === 0 ? (
          <div className={styles.empty}>No entries found for the selected filters.</div>
        ) : (
          <table className={styles.table}>
            <thead>
              <tr>
                <th>When</th>
                <th>Action</th>
                <th>Resource</th>
                <th><User size={12} style={{ marginRight: 4 }} />Actor</th>
                <th>Role</th>
                <th><Monitor size={12} style={{ marginRight: 4 }} />IP</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {entries.map(entry => (
                <>
                  <tr
                    key={entry.id}
                    className={`${styles.row} ${expanded === entry.id ? styles.rowExpanded : ''}`}
                    onClick={() => setExpanded(e => e === entry.id ? null : entry.id)}
                  >
                    <td className={styles.cellMono} title={new Date(entry.createdAt).toLocaleString()}>
                      {formatRelative(entry.createdAt)}
                    </td>
                    <td>
                      <span className={styles.actionBadge} style={{ color: actionColor(entry.action), borderColor: actionColor(entry.action) + '40', background: actionColor(entry.action) + '12' }}>
                        {entry.action}
                      </span>
                    </td>
                    <td>
                      <span style={{ color: 'var(--color-text-2)', fontSize: '0.8125rem' }}>{entry.resourceType ?? '—'}</span>
                      {entry.resourceId && <span className={styles.resourceId}>{entry.resourceId.slice(0, 8)}…</span>}
                    </td>
                    <td>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                        <div className={styles.avatar}>{entry.actorEmail[0]?.toUpperCase()}</div>
                        <span className={styles.email}>{entry.actorEmail}</span>
                        {entry.impersonatedBy && (
                          <span title={`Support session by ${entry.impersonatedBy}`}>
                            <Shield size={12} style={{ color: '#f59e0b' }} />
                          </span>
                        )}
                      </div>
                    </td>
                    <td><span className={styles.roleBadge}>{entry.actorRole}</span></td>
                    <td className={styles.cellMono}>{entry.ipAddress ?? '—'}</td>
                    <td style={{ color: 'var(--color-text-4)', fontSize: '0.75rem' }}>
                      {expanded === entry.id ? '▲' : '▼'}
                    </td>
                  </tr>
                  {expanded === entry.id && (
                    <tr key={`${entry.id}-detail`} className={styles.detailRow}>
                      <td colSpan={7}>
                        <div className={styles.detail}>
                          <div className={styles.detailGrid}>
                            <div>
                              <span className={styles.detailLabel}>Resource ID</span>
                              <code className={styles.detailValue}>{entry.resourceId ?? '—'}</code>
                            </div>
                            <div>
                              <span className={styles.detailLabel}>Timestamp</span>
                              <code className={styles.detailValue}>{new Date(entry.createdAt).toISOString()}</code>
                            </div>
                            <div>
                              <span className={styles.detailLabel}>User-Agent</span>
                              <code className={styles.detailValue}>{entry.userAgent ?? '—'}</code>
                            </div>
                            {entry.impersonatedBy && (
                              <div>
                                <span className={styles.detailLabel}>⚠️ Support session by</span>
                                <code className={styles.detailValue}>{entry.impersonatedBy}</code>
                              </div>
                            )}
                          </div>
                          {entry.changes && Object.keys(entry.changes).length > 0 && (
                            <div style={{ marginTop: 10 }}>
                              <span className={styles.detailLabel}>Changes</span>
                              <pre className={styles.changesBox}>{JSON.stringify(entry.changes, null, 2)}</pre>
                            </div>
                          )}
                        </div>
                      </td>
                    </tr>
                  )}
                </>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Pagination */}
      {data && data.pages > 1 && (
        <div className={styles.pagination}>
          <button
            className={styles.pageBtn}
            disabled={page <= 1}
            onClick={() => setPage(p => p - 1)}
          >
            <ChevronLeft size={16} /> Prev
          </button>
          <span style={{ fontSize: '0.8125rem', color: 'var(--color-text-3)' }}>
            Page {data.page} / {data.pages}
          </span>
          <button
            className={styles.pageBtn}
            disabled={page >= data.pages}
            onClick={() => setPage(p => p + 1)}
          >
            Next <ChevronRight size={16} />
          </button>
        </div>
      )}
    </div>
  );
}
